import { motion } from "framer-motion";
import { MapPin, Sparkles } from "lucide-react";
import { experiences, accentTokens } from "../data/mock";

export const Experience = () => {
  return (
    <section
      id="experience"
      className="relative py-24 md:py-32"
      data-testid="experience-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300">
            // 02 — Experience
          </p>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl font-bold tracking-tighter text-white">
            Where I&rsquo;ve built{" "}
            <span className="text-gradient-aurora animate-gradient-pan font-serif italic">
              things.
            </span>
          </h2>
          <p className="mt-4 max-w-2xl text-cyan-100/70 text-base sm:text-lg leading-relaxed">
            Three roles, three production AI/ML stacks — research to deployment.
          </p>
        </motion.div>

        <div className="mt-16 relative">
          {/* Vertical timeline line — gradient */}
          <div className="absolute left-3 md:left-4 top-2 bottom-2 w-px bg-gradient-to-b from-violet-400/60 via-cyan-400/40 to-pink-400/30" />

          <ol className="space-y-10 md:space-y-12">
            {experiences.map((exp, idx) => {
              const t = accentTokens[exp.accent] || accentTokens.cyan;
              return (
                <motion.li
                  key={`${exp.company}-${idx}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{
                    duration: 0.7,
                    delay: idx * 0.05,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="relative pl-12 md:pl-16"
                  data-testid={`experience-card-${idx}`}
                >
                  {/* Dot */}
                  <span className="absolute left-0 md:left-1 top-6 flex items-center justify-center h-7 w-7 rounded-full glass-strong">
                    <span className={`h-2 w-2 rounded-full bg-gradient-to-br ${t.ring.replace(/from-|via-|to-/g, "from-").split(" ")[0]} animate-pulse-soft`} />
                    <span className={`absolute inset-0 rounded-full bg-gradient-to-br ${t.ring} opacity-50 blur-md`} />
                  </span>

                  <div className={`relative gradient-border rounded-2xl glass p-6 sm:p-8 hover:bg-white/[0.05] transition-colors duration-300`}>
                    {/* corner accent blob */}
                    <div className={`absolute -top-10 -right-10 h-32 w-32 ${t.blob} blur-2xl rounded-full pointer-events-none`} />

                    <div className="relative grid md:grid-cols-12 gap-6 items-start">
                      <div className="md:col-span-3">
                        <p className="font-mono text-xs text-cyan-200/70 uppercase tracking-widest">
                          {exp.period}
                        </p>
                        <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-cyan-200/60">
                          <MapPin className="h-3 w-3" />
                          {exp.location}
                        </p>
                      </div>

                      <div className="md:col-span-9">
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                          <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white">
                            {exp.role}
                          </h3>
                          <span className="font-mono text-xs text-cyan-300/60">@</span>
                          <p className={`font-mono text-sm font-medium ${t.text}`}>
                            {exp.company}
                          </p>
                        </div>

                        <ul className="mt-5 space-y-2.5">
                          {exp.bullets.map((b, i) => (
                            <li
                              key={i}
                              className="relative pl-5 text-cyan-100/85 text-sm sm:text-base leading-relaxed"
                            >
                              <Sparkles className={`absolute left-0 top-1.5 h-3 w-3 ${t.text}`} />
                              {b}
                            </li>
                          ))}
                        </ul>

                        <div className="mt-5 flex flex-wrap gap-2">
                          {exp.stack.map((s) => (
                            <span
                              key={s}
                              className={`font-mono text-[11px] px-2.5 py-1 rounded-full border ${t.chip}`}
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
};
